import { unref } from 'vue';
import type { MpfFormFieldConfig } from '../components/MpfForm/MpfForm.props';
import { useMedicalRequestStore } from '../../stores/useMedicalRequestStore';
import { useAdminRequestStore } from '../../stores/useAdminRequestStore';
import { useRepeatPrescriptionRequestStore } from '../../stores/useRepeatPrescriptionRequestStore';
import { MpfFormPropsWithConfig } from '../../types/router-meta';

// Store type
type MedicalRequestStore = ReturnType<typeof useMedicalRequestStore>;
type AdminRequestStore = ReturnType<typeof useAdminRequestStore>;
type RepeatPrescriptionStore = ReturnType<typeof useRepeatPrescriptionRequestStore>;

type AnyStore = MedicalRequestStore | AdminRequestStore | RepeatPrescriptionStore;

type NestedKey = keyof AnyStore | string;

/** Determine if a value should be treated as "populated". */
function isPopulated(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== null && value !== undefined;
}

/** Resolve which store to use. Expand here when others exist. */
function resolveStore(storeId: string): AnyStore {
  if (storeId === 'medical-request') {
    return useMedicalRequestStore();
  } else if (storeId === 'admin-request') {
    return useAdminRequestStore();
  } else if (storeId === 'repeat-prescription-request') {
    return useRepeatPrescriptionRequestStore();
  }

  // TODO: attach additional stores when ready.
  throw new Error(`Unknown storeId: ${storeId}`);
}

function cloneForm<TForm extends { fields: MpfFormFieldConfig[] }>(form: TForm): TForm {
  return {
    ...(form as TForm),
    fields: form.fields.map((f) => ({ ...f })),
  };
}

function getValueFromStore(
  fieldName: string,
  store: AnyStore,
  preferredNestedKey?: NestedKey | NestedKey[],
): unknown {
  if (fieldName in store) {
    const direct = unref((store as any)[fieldName]);
    if (isPopulated(direct)) return direct;
  }

  const preferredKeys: string[] = preferredNestedKey
    ? Array.isArray(preferredNestedKey)
      ? preferredNestedKey.map((k) => String(k))
      : [String(preferredNestedKey)]
    : [];

  if (preferredKeys.length) {
    for (const prefKey of preferredKeys) {
      if (!(prefKey in store)) continue;

      const topVal = unref((store as any)[prefKey]);

      if (
        topVal &&
        typeof topVal === 'object' &&
        !Array.isArray(topVal) &&
        !(topVal instanceof Date)
      ) {
        if (fieldName in topVal) {
          const nested = (topVal as any)[fieldName];
          if (isPopulated(nested)) return nested;
        }
      }
    }

    return undefined;
  }

  for (const topKey of Object.keys(store)) {
    const topVal = unref((store as any)[topKey]);

    if (
      topVal &&
      typeof topVal === 'object' &&
      !Array.isArray(topVal) &&
      !(topVal instanceof Date)
    ) {
      if (fieldName in topVal) {
        const nested = (topVal as any)[fieldName];
        if (isPopulated(nested)) return nested;
      }
    }
  }

  return undefined;
}

export function useMergeDataIntoForm() {
  function mergeDataIntoForm<TForm extends { fields: MpfFormFieldConfig[] }>(
    form: MpfFormPropsWithConfig,
    storeId: string,
    preferredNestedKey?: NestedKey | NestedKey[],
  ): TForm {
    const store = resolveStore(storeId);
    const merged = cloneForm(form);

    merged.fields.forEach((field: MpfFormFieldConfig) => {
      const value = getValueFromStore(field.name, store, preferredNestedKey);

      if (value === undefined) return;

      if (field.type === 'attachments') {
        field.initialValue =
          Array.isArray(value) && value.every((v) => v instanceof File) ? value : [];
        return;
      }

      // --- Checkboxes: ALWAYS an array ---
      if (field.type === 'checkboxes') {
        if (Array.isArray(value)) {
          field.initialValue = value;
        } else if (value != null) {
          field.initialValue = [value] as string[];
        }
        return;
      }

      // --- Radios ---
      if (field.type === 'radios') {
        field.initialValue = value as any;
        return;
      }

      field.initialValue = value as any;
    });

    return merged as TForm;
  }

  return {
    mergeDataIntoForm,
  };
}
